<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import Button from "../../../base-components/Button";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { Menu } from "../../../base-components/Headless";
import { usePedidosStore } from "../../../services/pedidos";
import { useMesasStore } from "../../../services/mesas";
import { useSectoresStore } from "../../../services/sectores";
import ModalPedidosVer from "./modals/ModalPedidosVer.vue";
import { userUserStore } from "../../../stores/user";
import { numeroFormateado, printHTMLContent } from "../../../utils/helper";
import {
  formatearFechaAAAAMMDD,
  formatearFechaTimestampDDMMYYYY
} from "../../../utils/fecha";
import router from "../../../router";
import { storeToRefs } from "pinia";
import PedidosListadoTotales from "./PedidosListadoTotales.vue";
import { pedidoConverter, Pedido } from "@fixeduy/restaurantestypes";
import ExcelJS from "exceljs";
import ModalPedidosLiveModificarEstado from "../../PedidosLive/components/modals/ModalPedidosLiveModificarEstado.vue";
import { getEstadoPedido } from "../../../constants/estadosPedido";
import { useCollection } from "vuefire";
import {
  type QueryConstraint,
  collection,
  getFirestore,
  limit,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { utilFechaGetTimestamp } from "../../../utils/fechaFixedTypes";
import ModalPedidosAnularFactura from "./modals/ModalPedidosAnularFactura.vue";
import ModalPedidosFacturar from "./modals/ModalPedidosFacturar.vue";
import { useMensajesStore } from "../../../services/mensajes";
import {
  ApiFixedImprimirTicket,
  imprimirCFETicket
} from "../../../services/cloudFunctions/fixed/apiFixedImprimirTicket";

const pedidosStore = usePedidosStore();
const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const { filtros } = storeToRefs(pedidosStore);
const userStore = userUserStore();
const mensajesStore = useMensajesStore();
const tieneFiltro = ref(false);
const db = getFirestore();

onBeforeMount(async () => {
  if (!mesasStore.mesas.length && !mesasStore.cargando) {
    await mesasStore.getFromFirestore();
  }
  if (!sectoresStore.sectores.length && !sectoresStore.cargando) {
    await sectoresStore.getFromFirestore();
  }

  const url = router.currentRoute.value.fullPath;
  const queryString = url.substring(url.indexOf("?") + 1);

  const params = new URLSearchParams(queryString);

  let filtro = false;
  if (queryString != "/pedidos" && queryString != "/pedidos/") filtro = true;

  const parametros: { [key: string]: string | null } = {};

  for (const [key, value] of params) {
    parametros[key] = value;
  }

  tieneFiltro.value = filtro;

  filtros.value.fechaInicio = "";
  filtros.value.fechaFin = "";
  filtros.value.horaInicio = "";
  filtros.value.horaFin = "";
  filtros.value.cliente = null;
  filtros.value.numero = "";
  filtros.value.origen = "";
  filtros.value.estado = "";

  if (filtro) {
    filtros.value.fechaInicio = parametros.fechaInicio ?? "";
    filtros.value.fechaFin = parametros.fechaFin ?? "";
    filtros.value.horaInicio = parametros.horaInicio ?? "";
    filtros.value.horaFin = parametros.horaFin ?? "";
    filtros.value.origen = (parametros.origen as any) ?? "";
    filtros.value.numero = parametros.numero ?? "";
    filtros.value.estado = (parametros.estado as Pedido["estado"]) ?? "";
  }
});

const parseHoraMinuto = (
  hora: string | null | undefined,
  porDefecto: { h: number; m: number }
) => {
  const source = (hora ?? "").trim();
  const match = source.match(/(\d{1,2}):(\d{2})/);
  if (!match) return porDefecto;

  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return porDefecto;
  if (h < 0 || h > 23 || m < 0 || m > 59) return porDefecto;

  return { h, m };
};

const getTimestampDesdeFechaHora = (
  fechaDDMMYYYY: string,
  hora: string | null | undefined,
  esFin: boolean
) => {
  const fechaNormalizada = formatearFechaAAAAMMDD(fechaDDMMYYYY);
  const [yyyy, mm, dd] = fechaNormalizada.split("-").map(Number);

  const horaMinuto = parseHoraMinuto(
    hora,
    esFin ? { h: 23, m: 59 } : { h: 0, m: 0 }
  );
  return utilFechaGetTimestamp(
    yyyy,
    mm,
    dd,
    horaMinuto.h,
    horaMinuto.m,
    esFin ? 59 : 0
  );
};

const pedidosQuery = computed(() => {
  const empresaId = userStore.empresa?.id;
  if (!empresaId) return null;

  const constraints: QueryConstraint[] = [];

  if (filtros.value.fechaInicio != "" && filtros.value.fechaInicio != "null") {
    const fechaInicio = getTimestampDesdeFechaHora(
      filtros.value.fechaInicio,
      filtros.value.horaInicio,
      false
    );
    constraints.push(where("fechaCreacion", ">=", fechaInicio));
  }

  if (filtros.value.fechaFin != "" && filtros.value.fechaFin != "null") {
    const fechaFin = getTimestampDesdeFechaHora(
      filtros.value.fechaFin,
      filtros.value.horaFin,
      true
    );
    constraints.push(where("fechaCreacion", "<=", fechaFin));
  }

  if (filtros.value.numero != "") {
    const numero = parseInt(filtros.value.numero);
    if (!Number.isNaN(numero)) {
      constraints.push(where("numero", "==", numero));
    }
  }

  if (filtros.value.estado != "") {
    constraints.push(where("estado", "==", filtros.value.estado));
  }

  constraints.push(orderBy("fechaCreacion", "desc"));
  constraints.push(orderBy("numero", "desc"));

  // Mimicamos la lógica anterior del store:
  // - sin filtros: solo los últimos (200)
  // - con filtros: intentaba paginar hasta un máximo (aprox. 10k)
  constraints.push(limit(tieneFiltro.value ? 10000 : 200));

  return query(
    collection(db, `empresas/${empresaId}/pedidos`).withConverter(
      pedidoConverter
    ),
    ...constraints
  );
});

const pedidos = useCollection(pedidosQuery);
const pedidosFiltrados = computed(() => {
  const sectorId = filtros.value.origen;
  if (!sectorId) return pedidos.value ?? [];

  const mesasSectorIds = new Set(
    mesasStore.mesas
      .filter(mesa => mesa.sectorId === sectorId)
      .map(mesa => mesa.id)
  );

  return (pedidos.value ?? []).filter(pedido =>
    mesasSectorIds.has(pedido.mesaId ?? "")
  );
});

const getSectorNombre = (pedido: Pedido) => {
  const mesa = mesasStore.mesas.find(m => m.id === pedido.mesaId);
  if (!mesa?.sectorId) return "Sin sector";
  return (
    sectoresStore.sectores.find(s => s.id === mesa.sectorId)?.nombre ??
    "Sin sector"
  );
};

enum showModalType {
  none,
  Ver,
  ModificarEstado,
  Anular,
  Facturar
}

const modal = ref<showModalType>(showModalType.none);
const pedidoSeleccionado = ref<Pedido | null>(null);

const showModal = (pedido: Pedido, modalShow: showModalType) => {
  modal.value = modalShow;
  pedidoSeleccionado.value = pedido;
};

const cerrarModal = async () => {
  modal.value = showModalType.none;
  pedidoSeleccionado.value = null;
};

const formatearDuracion = (inicio: number, fin: number) => {
  // Diferencia en segundos
  const segundos = Math.floor((fin - inicio) / 1000);

  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const seg = Math.floor(segundos % 60);

  const hh = horas.toString().padStart(2, "0");
  const mm = minutos.toString().padStart(2, "0");
  const ss = seg.toString().padStart(2, "0");

  return `${hh}:${mm}:${ss}`;
  // return `${hh}:${mm}`;
};

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Hoja 1");
  const encabezados = [
    "# Nro Pedido",
    "Sector",
    "Mesa",
    "Detalle",
    "Cantidad",
    "Fecha creación",
    "Fecha finalizada",
    "Duración (hh:mm)",
    "Estado",
    "P. unitario",
    "Total",
    "Categoría"
  ];

  worksheet.addRow(encabezados);

  for (const pedido of pedidosFiltrados.value ?? []) {
    const lineas = await pedidosStore.getPedidoById(pedido.id);

    lineas.forEach((linea, index) => {
      if (index === 0) {
        // Primera fila: datos del pedido + primera línea
        worksheet.addRow([
          pedido.numero,
          getSectorNombre(pedido),
          pedido.mesaNombre,
          linea.nombre,
          linea.cantidad,
          formatearFechaTimestampDDMMYYYY(pedido.fechaCreacion, true),
          pedido.fechaCierre
            ? formatearFechaTimestampDDMMYYYY(pedido.fechaCierre, true)
            : "",
          pedido.fechaCreacion && pedido.fechaCierre
            ? formatearDuracion(pedido.fechaCreacion, pedido.fechaCierre)
            : "",
          pedido.estado,
          `$${numeroFormateado(linea.precioUnitarioConIva)}`,
          `$${numeroFormateado(linea.total)}`,
          linea.rubro
        ]);
      } else {
        // Filas siguientes: solo datos de línea
        worksheet.addRow([
          "",
          "",
          "",
          linea.nombre,
          linea.cantidad,
          "",
          "",
          "",
          "",
          `$${numeroFormateado(linea.precioUnitarioConIva)}`,
          `$${numeroFormateado(linea.total)}`,
          linea.rubro
        ]);
      }
    });
  }
  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Pedidos - ${filtros.value.fechaInicio}- ${filtros.value.fechaFin}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};

const mostrarImpresion = async (pedido: Pedido, devolucion: boolean) => {
  let idCFE = pedido.cfeId;
  if (devolucion) {
    idCFE = pedido.cfeIdDevolucion;
  }
  if (!idCFE || idCFE == "") {
    mensajesStore.mostrarModal(
      "No es posible generar la impresión",
      "El comprobante no se generó desde el sistema de Atiendo",
      mensajesStore.mensajeType.error
    );
    return;
  }
  mensajesStore.mostrarToast(
    `Cargando impresión...`,
    `${devolucion ? "Devolución" : ""} Pedido # ${pedido.numero.toString()}`,
    mensajesStore.mensajeType.exito
  );

  let data: ApiFixedImprimirTicket = {
    apiKey: "JU4L6W22-0P8A-G5F3-PN3Z-438UNRWV9G5J",
    rutComercio: userStore.empresa?.rut!,
    idCFE: idCFE
  };
  const respuesta = await imprimirCFETicket(data);
  if (respuesta) {
    await printHTMLContent(respuesta.htmlTicket);
  }
};
</script>

<template>
  <div class="flex justify-end">
    <Button class="mt-4" variant="secondary" @click="handleExport"
      >Exportar a Excel</Button
    >
  </div>
  <Preview class="mt-4 box">
    <template v-if="pedidosFiltrados?.length > 10">
      <PedidosListadoTotales :pedidos="pedidosFiltrados" />
    </template>

    <div class="px-5 min-w-0">
      <Preview.Panel>
        <div class="overflow-x-auto min-w-0 -mx-5 px-5">
          <Table hover class="min-w-[700px]">
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap"> # Nro Pedido</Table.Th>
                <Table.Th class="whitespace-nowrap"> Sector</Table.Th>
                <Table.Th class="whitespace-nowrap"> Mesa</Table.Th>
                <Table.Th class="whitespace-nowrap"> Fecha creación </Table.Th>
                <Table.Th class="whitespace-nowrap">
                  Fecha finalizada
                </Table.Th>
                <Table.Th class="whitespace-nowrap"> Duración (hh:mm)</Table.Th>
                <Table.Th class="whitespace-nowrap"> Estado </Table.Th>
                <Table.Th class="whitespace-nowrap text-end"> Total </Table.Th>
                <Table.Th class="whitespace-nowrap text-end">
                  Facturado
                </Table.Th>

                <Table.Th class="whitespace-nowrap"> </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <template v-for="pedido in pedidosFiltrados" :key="pedido.id">
                <Table.Tr
                  @click="showModal(pedido, showModalType.Ver)"
                  class="cursor-pointer"
                >
                  <Table.Td># {{ pedido.numero }} </Table.Td>
                  <Table.Td>{{ getSectorNombre(pedido) }}</Table.Td>
                  <Table.Td>{{ pedido.mesaNombre }}</Table.Td>
                  <Table.Td>{{
                    formatearFechaTimestampDDMMYYYY(pedido.fechaCreacion, true)
                  }}</Table.Td>
                  <Table.Td>
                    {{
                      pedido.fechaCierre
                        ? formatearFechaTimestampDDMMYYYY(
                            pedido.fechaCierre,
                            true
                          )
                        : ""
                    }}
                  </Table.Td>
                  <Table.Td>
                    {{
                      pedido.fechaCreacion && pedido.fechaCierre
                        ? formatearDuracion(
                            pedido.fechaCreacion,
                            pedido.fechaCierre
                          )
                        : ""
                    }}
                  </Table.Td>
                  <Table.Td>
                    <span
                      v-if="getEstadoPedido(pedido.estado)"
                      class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border"
                      :class="getEstadoPedido(pedido.estado)?.badgeClasses"
                    >
                      {{ getEstadoPedido(pedido.estado)?.label }}
                    </span>
                    <span v-else>{{ pedido.estado }}</span>
                  </Table.Td>
                  <Table.Td class="text-end"
                    >$ {{ numeroFormateado(pedido.total) }}</Table.Td
                  >
                  <Table.Td class="text-end">
                    {{
                      pedido.facturado && !pedido.devuelto
                        ? "Si"
                        : pedido.facturado && pedido.devuelto
                          ? "Facturada y anulada"
                          : "No"
                    }}</Table.Td
                  >

                  <Table.Td @click.stop>
                    <div class="flex justify-end">
                      <Menu>
                        <Menu.Button :as="Button" class="px-2 !box">
                          <span class="flex items-right justify-center w-5 h-5">
                            <Lucide icon="Plus" class="w-4 h-4" />
                          </span>
                        </Menu.Button>
                        <Menu.Items class="w-40" placement="left-start">
                          <Menu.Item
                            @click="showModal(pedido, showModalType.Ver)"
                          >
                            <Lucide icon="Eye" class="w-4 h-4 mr-2" />
                            Ver pedido
                          </Menu.Item>
                          <Menu.Item
                            @click="
                              showModal(pedido, showModalType.ModificarEstado)
                            "
                          >
                            <Lucide icon="Edit" class="w-4 h-4 mr-2" />
                            Editar pedido
                          </Menu.Item>
                          <Menu.Item
                            @click="showModal(pedido, showModalType.Facturar)"
                            v-if="
                              !pedido.facturado &&
                              pedido.estado == 'cerrado' &&
                              userStore.opcionesGenerales.facturaFixed
                            "
                          >
                            <Lucide icon="Receipt" class="w-4 h-4 mr-2" />
                            Facturar
                          </Menu.Item>
                          <Menu.Item
                            v-if="
                              pedido.facturado &&
                              pedido.estado == 'cerrado' &&
                              userStore.opcionesGenerales.facturaFixed &&
                              pedido.cfeId &&
                              pedido.cfeId != ''
                            "
                            @click="mostrarImpresion(pedido, false)"
                          >
                            <Lucide icon="Printer" class="w-4 h-4 mr-2" />
                            Imprimir factura
                          </Menu.Item>
                          <Menu.Item
                            v-if="
                              pedido.facturado &&
                              pedido.devuelto &&
                              pedido.estado == 'cerrado' &&
                              userStore.opcionesGenerales.facturaFixed &&
                              pedido.cfeIdDevolucion &&
                              pedido.cfeIdDevolucion != ''
                            "
                            @click="mostrarImpresion(pedido, true)"
                          >
                            <Lucide icon="Printer" class="w-4 h-4 mr-2" />
                            Imprimir devolucion
                          </Menu.Item>
                          <Menu.Item
                            @click="showModal(pedido, showModalType.Anular)"
                            v-if="
                              pedido.estado == 'cerrado' &&
                              pedido.facturado &&
                              !pedido.devuelto &&
                              pedido.cfeId &&
                              pedido.cfeId != ''
                            "
                            class="text-danger"
                          >
                            <Lucide icon="Receipt" class="w-4 h-4 mr-2" />
                            Anular factura
                          </Menu.Item>
                        </Menu.Items>
                      </Menu>
                    </div>
                  </Table.Td>
                </Table.Tr>
              </template>
              <Table.Tr v-if="pedidosFiltrados.length === 0">
                <Table.Td colspan="30" class="text-center py-8 text-slate-500">
                  No hay pedidos para mostrar.
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
        <template v-if="pedidosFiltrados.length > 0">
          <PedidosListadoTotales :pedidos="pedidosFiltrados" />
        </template>
      </Preview.Panel>
    </div>
    <ModalPedidosVer
      v-if="modal == showModalType.Ver && pedidoSeleccionado != null"
      :pedido="pedidoSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalPedidosLiveModificarEstado
      v-if="
        modal == showModalType.ModificarEstado && pedidoSeleccionado != null
      "
      :pedido="pedidoSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalPedidosAnularFactura
      v-if="modal == showModalType.Anular && pedidoSeleccionado != null"
      :pedido="pedidoSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalPedidosFacturar
      v-if="modal == showModalType.Facturar && pedidoSeleccionado != null"
      :pedido="pedidoSeleccionado!"
      @cerrar="cerrarModal"
    />
  </Preview>
</template>

<style scoped>
.bloqueado {
  color: rgb(185 28 28);
  padding: 0 0 0 10px;
}
</style>
